import { useCallback, useEffect, useRef, useState } from "react";

interface UseLivePollingOptions<T> {
  /** Async fetcher; receives an AbortSignal so requests can be cancelled. */
  fetcher: (signal: AbortSignal) => Promise<T>;
  /** Polling interval in milliseconds. */
  intervalMs: number;
  /** When false, no fetching happens (e.g. wait until session is ready). */
  enabled?: boolean;
  /** Optional dependency list that triggers a fresh poll when changed. */
  deps?: ReadonlyArray<unknown>;
}

interface UseLivePollingResult<T> {
  data: T | null;
  /**
   * True only while the very first fetch is in flight (so a tile can
   * render a one-time skeleton). Background polls intentionally do
   * NOT flip this back to true — that would make the UI flash
   * "Loading…" on every refresh.
   */
  loading: boolean;
  /** True whenever a poll (first or subsequent) is in flight. */
  refreshing: boolean;
  error: unknown;
  /** Forces an immediate refresh, returning the new data once settled. */
  refresh: () => Promise<T | null>;
}

/**
 * Generic polling helper for the dashboard widgets. Each tile owns
 * its own poll loop so a slow KPI fetch can't block the snappy live
 * widgets from refreshing. The hook keeps the previous payload while
 * a new one is in flight so the UI doesn't flash empty.
 */
export function useLivePolling<T>({
  fetcher,
  intervalMs,
  enabled = true,
  deps = [],
}: UseLivePollingOptions<T>): UseLivePollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const runOnce = useCallback(async (signal: AbortSignal): Promise<T | null> => {
    // Only the very first poll flips `loading` to true so tiles can
    // render a skeleton state once. Subsequent ticks update only
    // `refreshing` so the existing data keeps rendering with no
    // visible flash while we re-fetch in the background.
    setRefreshing(true);
    try {
      const result = await fetcherRef.current(signal);
      if (!signal.aborted) {
        setData(result);
        setError(null);
      }
      return result;
    } catch (err) {
      if (!signal.aborted) {
        setError(err);
      }
      return null;
    } finally {
      if (!signal.aborted) {
        setRefreshing(false);
        // First poll has now resolved (success or error) — drop the
        // skeleton flag. The error state takes over if it failed.
        setLoading(false);
      }
    }
  }, []);

  const externalControllerRef = useRef<AbortController | null>(null);
  const refresh = useCallback(async (): Promise<T | null> => {
    externalControllerRef.current?.abort();
    const controller = new AbortController();
    externalControllerRef.current = controller;
    return runOnce(controller.signal);
  }, [runOnce]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Each polling tick gets its own AbortController so a single
    // cancelled request (e.g. unmount, dependency change, react
    // strict-mode double-mount in dev) doesn't permanently kill the
    // polling loop. The outer `cancelled` flag stops any tick that's
    // still pending after cleanup from updating state.
    let cancelled = false;
    const inFlight = new Set<AbortController>();

    const tick = () => {
      if (cancelled) return;
      const controller = new AbortController();
      inFlight.add(controller);
      void runOnce(controller.signal).finally(() => {
        inFlight.delete(controller);
      });
    };

    tick();
    const id = window.setInterval(tick, intervalMs);

    return () => {
      cancelled = true;
      for (const c of inFlight) c.abort();
      inFlight.clear();
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, runOnce, ...deps]);

  return { data, loading, refreshing, error, refresh };
}
